import { ReactNode } from "react";
import LoadingOrError from "../Fetch/LoadingOrError.tsx";
import { useGet } from "../Fetch/useFetch.tsx";
import MyFormik from "../Form/MyFormik.tsx";
import { Form } from "../Form/Form.tsx";
import toast from "../../module/Toast/toast.tsx";
import Modal, { ActionBar, Content } from "./Modal.tsx";
import styles from "./Modal.module.css";

const EditModalBody = ({
  prepareVariables,
  getInitialValues,
  query,
  // loadData = données "à charger ou non" -> si true alors on fait un useGet pour avoir les valeurs
  loadData,
  id,
  validationSchema,
  isPost = true,
  canModify,
  onSubmit,
  closeModal,
  children,
  submitLabel,
  isMultipartFormData = false,
}: EditModalType) => {
  const isAdd = id == null;
  const submitUrl = query;
  let initialData = {};

  if (!isAdd && loadData) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const editState = useGet(submitUrl);
    const { isResolved, data = {} } = editState;

    if (!isResolved) {
      return <LoadingOrError state={editState} />;
    }

    if (!data) {
      toast.error(
        "Une erreur est survenue lors de la récupération des données.",
      );
      closeModal();
    }
    initialData = data;
  }

  return (
    <MyFormik
      validationSchema={validationSchema}
      isPost={isPost}
      initialValues={
        !loadData
          ? getInitialValues
          : getInitialValues
            ? getInitialValues(initialData)
            : initialData
      }
      submitUrl={submitUrl}
      onSubmit={(data) => {
        onSubmit && onSubmit(data);
        closeModal();
      }}
      prepareVariables={(values) =>
        isAdd ? prepareVariables(values) : prepareVariables(id, values)
      }
      isMultipartFormData={isMultipartFormData}
    >
      <Form>
        <Content>{children}</Content>
        <ActionBar>
          {canModify ? (
            <>
              <button
                className={styles.closeButton}
                onClick={(e) => {
                  e.preventDefault();
                  closeModal();
                }}
              >
                Annuler
              </button>
              <button className={styles.active} type="submit">
                {submitLabel ?? "Valider"}
              </button>
            </>
          ) : (
            <button className={styles.active} onClick={closeModal}>
              Fermer
            </button>
          )}
        </ActionBar>
      </Form>
    </MyFormik>
  );
};

const EditModal = ({
  visible,
  closeModal,
  header,
  query,
  loadData,
  id,
  children,
  validationSchema,
  isPost = true,
  onSubmit,
  prepareVariables = (values) => ({ values }),
  getInitialValues,
  canModify = true,
  submitLabel,
  isMultipartFormData = false,
}: EditModalType) => {
  return (
    <Modal {...{ visible, header, closeModal }}>
      <EditModalBody
        canModify={canModify}
        query={query}
        loadData={loadData}
        id={id}
        validationSchema={validationSchema}
        isPost={isPost}
        onSubmit={onSubmit}
        closeModal={closeModal}
        prepareVariables={prepareVariables}
        getInitialValues={getInitialValues}
        submitLabel={submitLabel}
        isMultipartFormData={isMultipartFormData}
      >
        {children}
      </EditModalBody>
    </Modal>
  );
};

type EditModalType = {
  visible: boolean;
  header?: ReactNode | undefined;
  closeModal: () => boolean;
  loadData: boolean;
  id?: string | undefined;
  query: string;
  onDelete?: () => void | undefined;
  content?: string | undefined;
  children: ReactNode;
  onSubmit: (data: object) => void;
  validationSchema: object;
  isPost: boolean;
  prepareVariables: (...data: object[]) => object[];
  getInitialValues: (data: object) => object[];
  queryVariables?: object[] | undefined;
  canModify: boolean;
  query: string;
  submitLabel?: string | ReactNode | undefined;
  isMultipartFormData: boolean;
};

export default EditModal;
